/**
 * @file        src/pages/package/PackageList.jsx
 * @description Paginated package directory. Columns mirror the legacy screen:
 *              # · Image · Name · Status · Language · Updated On · Action.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Package Management: Package List slice).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function PackageList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [filters, setFilters] = useState({ q: '' });
  const [applied, setApplied] = useState(filters);

  const qc = useQueryClient();
  const params = { ...applied, page, pageSize };
  const { data, isLoading } = useQuery({
    queryKey: qk.packages.list(params),
    queryFn: () => get('/packages', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/packages/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Package removed.');
      qc.invalidateQueries({ queryKey: ['packages'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete package'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setApplied(filters);
  }

  async function onDelete(row) {
    if (!(await confirm({
      title: 'Delete package',
      message: `Delete package “${row.name}”? This cannot be undone.`,
    }))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Package List"
        subtitle="Package Management · holiday packages"
        actions={
          <Link to="/package/new" className="btn primary"><Icon name="plus" /> Add Package</Link>
        }
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={applyFilters}>
          <div className="field">
            <label>Name / Destination</label>
            <input placeholder="e.g. Bali" value={filters.q}
                   onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Image</th><th>Name</th><th>Status</th><th>Language</th><th>Updated On</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No packages yet. Add your first one.</td></tr>
                ) : rows.map((p, idx) => (
                  <tr key={p.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>
                      {p.hero_image
                        ? <img src={p.hero_image} alt={p.name} className="thumb"
                               style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      <b>{p.name}</b>
                      {p.destination ? <small className="muted db">{p.destination}</small> : null}
                    </td>
                    <td><Chip tone={p.is_active ? 'ok' : 'rej'}>{p.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td className="mono">{p.language}</td>
                    <td>{fmtDate(p.updated_at)}</td>
                    <td className="row-act">
                      <Link to={`/package/${p.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(p)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
